import axios from './_axios'
import {prepareParams} from './_internal'
import {AxiosResult, Paginate} from './_types'

const prefix = process.env.REACT_APP_COMPANIES_BASE_URL

export type AlarmCenterLineModel = 'AM10' | 'AM8' | 'PRO'

export type AlarmCenter = {
  id: number
  hardwareId: string
  accountNumber: string
  lineModel: AlarmCenterLineModel
  companyId: number
  clientId: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type GetAlarmCentersParams = {
  page?: number
  pageSize?: number
  filter?: {
    id?: number
    companyId?: number
    clientId?: number
    lineModel?: AlarmCenterLineModel
    isActive?: boolean
  }
  search?: {
    id?: string
    hardwareId?: string
    accountNumber?: string
  }
  orderBy?: {
    lineModel?: 'ASC' | 'DESC'
    createdAt?: 'ASC' | 'DESC'
  }
}

export type CreateAlarmCenter = {
  hardwareId: string
  accountNumber: string
  lineModel: AlarmCenterLineModel
  companyId: number
  clientId: number
  isActive?: boolean
}

export type PatchAlarmCenter = {
  hardwareId?: string
  accountNumber?: string
  lineModel?: AlarmCenterLineModel
  isActive?: boolean
}

export type AlarmCenterZone = {
  id: number
  alarmCenterId: number
  zoneNumber: number
  partition?: string | null
  name?: string | null
  cameraId: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type GetAlarmCenterZonesParams = {
  page?: number
  pageSize?: number
  filter?: {
    alarmCenterId?: number
    cameraId?: number
    zoneNumber?: number
    isActive?: boolean
  }
}

export type CreateAlarmCenterZone = {
  alarmCenterId: number
  zoneNumber: number
  cameraId: number
  partition: string
  name?: string | null
  isActive?: boolean
}

export type PatchAlarmCenterZone = {
  zoneNumber?: number
  cameraId?: number
  partition?: string | null
  name?: string | null
  isActive?: boolean
}

export class AlarmCenterEndpoint {
  async getMany(params: GetAlarmCentersParams): Promise<AxiosResult<Paginate<AlarmCenter>>> {
    return await axios.get(`${prefix}/alarm-center`, {
      params: prepareParams(params),
    })
  }

  async getById(id: number): Promise<AxiosResult<AlarmCenter>> {
    return await axios.get(`${prefix}/alarm-center/${id}`)
  }

  async createOne(body: CreateAlarmCenter): Promise<AxiosResult<AlarmCenter>> {
    return await axios.post(`${prefix}/alarm-center`, body)
  }

  async updateOne(id: number, body: PatchAlarmCenter): Promise<AxiosResult<AlarmCenter>> {
    return await axios.patch(`${prefix}/alarm-center/${id}`, body)
  }

  async deleteOne(id: number): Promise<AxiosResult<void>> {
    return await axios.delete(`${prefix}/alarm-center/${id}`)
  }

  async getZones(params: GetAlarmCenterZonesParams): Promise<AxiosResult<Paginate<AlarmCenterZone>>> {
    return await axios.get(`${prefix}/alarm-center-zone`, {
      params: prepareParams(params),
    })
  }

  async createZone(body: CreateAlarmCenterZone): Promise<AxiosResult<AlarmCenterZone>> {
    return await axios.post(`${prefix}/alarm-center-zone`, body)
  }

  async updateZone(id: number, body: PatchAlarmCenterZone): Promise<AxiosResult<AlarmCenterZone>> {
    return await axios.patch(`${prefix}/alarm-center-zone/${id}`, body)
  }

  async deleteZone(id: number): Promise<AxiosResult<void>> {
    return await axios.delete(`${prefix}/alarm-center-zone/${id}`)
  }
}
